import { Button, Form, Toast } from '@seiue/ui'
import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'

import { EntryComponentsWrapper } from 'packages/theme'

import { FormContext } from '../context'
import { FormContextParam, ReqWithMetaData } from '../types'

import { BaseFormItem } from './BaseFormItem'
import { QuestionnairePagination, getEmptyOtherInfoKey } from './utils'
import { formatOriginData } from '@/features/UserTasks/Questionnaire/utils'
import { QuestionnaireSubmitRes } from 'packages/sdks-next/growp'

/**
 * 问卷 - 表单区域
 *
 * @param param - 参数
 * @param param.questionnaires - 问卷结构
 * @param param.initData - 问卷数据
 * @returns React.ReactNode
 */
export const FormArea: React.FC<{
  questionnaires: ReqWithMetaData[]
  page: number
  setPage: (v: number) => void
  initData?: QuestionnaireSubmitRes[]
  isPreview?: boolean
}> = ({ questionnaires: originData, page, setPage, initData, isPreview }) => {
  const { data, total } = QuestionnairePagination(originData)
  const questionnaires = data[page]
  const { formValues, setFormValues, setFinalFormValues, otherInfo } =
    useContext<FormContextParam>(FormContext)
  const [form] = Form.useForm()

  useEffect(() => {
    form.resetFields()
    if (initData) {
      form.setFieldsValue({ ...formatOriginData(initData || []) })
    }
  }, [initData])

  useEffect(() => {
    form.setFieldsValue({ ...formValues })
    const ele = document.getElementById('questionnaire-top')
    ele && ele.scrollIntoView({ block: 'start' })
  }, [page])

  const scrollToErrorOtherInfo = () => {
    const key = getEmptyOtherInfoKey(otherInfo)
    let scrollKey = key
    if (key) {
      const [a, b, c] = key.split('.')
      if (b && c) {
        scrollKey = `${a}_${c}_${b}`
      }
      form.scrollToField(scrollKey, {
        block: 'center',
      })
      return 1
    }
    return 0
  }

  const onSaveData = (isNext?: boolean) => {
    const nextPage = isNext ? page + 1 : page - 1
    // 预览时不进行校验
    if (isPreview) {
      setPage(nextPage)
    } else {
      if (!isNext) {
        setPage(nextPage)
        return
      }
      form
        .validateFields()
        .then(() => {
          const num = scrollToErrorOtherInfo()
          if (!num) {
            setPage(nextPage)
          }
        })
        .catch((errInfo) => {
          if (errInfo.errorFields && errInfo.errorFields[0]) {
            const field = errInfo.errorFields[0].name.join('_')
            form.scrollToField(field, {
              block: 'center',
            })
          }
        })
    }
  }

  const onSubmit = () => {
    if (isPreview) {
      Toast.warning('预览模式下不支持数据提交')
      return
    }
    form
      .validateFields()
      .then(async (value) => {
        const num = await scrollToErrorOtherInfo()
        if (!num) {
          setFinalFormValues &&
            setFinalFormValues({ ...formValues, ...value }, otherInfo)
        }
      })
      .catch((errInfo) => {
        if (errInfo.errorFields && errInfo.errorFields[0]) {
          const field = errInfo.errorFields[0].name.join('_')
          form.scrollToField(field, {
            block: 'center',
          })
        }
      })
  }
  return (
    <Wrapper>
      <EntryComponentsWrapper>
        <Form
          form={form}
          onValuesChange={(v, values) => {
            setFormValues({ ...formValues, ...values })
          }}
        >
          {questionnaires.map((question, index) => {
            return (
              <BaseFormItem
                key={index}
                currentQuestion={question}
                currentIndex={index}
              />
            )
          })}
        </Form>
        {total === 1 ? (
          <ButtonWrapper>
            <StyledButton
              type="primary"
              onClick={() => onSubmit()}
            >
              提交
            </StyledButton>
          </ButtonWrapper>
        ) : (
          <PaginationWrapper>
            {page === 1 ? null : (
              <Button
                disabled={page === 1}
                onClick={async () => {
                  onSaveData()
                }}
              >
                上一页
              </Button>
            )}
            {page === total ? (
              <ButtonWrapper>
                <Button
                  type="primary"
                  onClick={() => onSubmit()}
                >
                  提交
                </Button>
              </ButtonWrapper>
            ) : (
              <Button
                disabled={page === total}
                onClick={async () => {
                  onSaveData(true)
                }}
              >
                下一页
              </Button>
            )}
          </PaginationWrapper>
        )}
      </EntryComponentsWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin-top: 24px;
`

const ButtonWrapper = styled.span`
  &&& {
    .se-button__primary {
      background-color: ${(props) => props.theme.brand._1} !important;
    }
  }
`

const StyledButton = styled(Button)`
  &&& {
    width: 320px;
    height: 40px;
    border-radius: 8px;
    margin-left: 220px !important;
  }
`

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  &&& {
    .se-button__default,
    .se-button__primary {
      width: 240px;
      height: 40px;
      margin-right: 12px;
    }
    .se-button__default[disabled],
    .se-button__default[disabled]:hover {
      background-color: ${(props) => props.theme.background._4};
    }
  }
`
